import { fetchApi, fetchGetFileApi } from '../client';
import { buildQS } from '../helpers';
import type { Appointment, CreateAppointmentRequest, AppointmentStatus } from '../../../types';
import type { BookingCounts, BookingInbox, BookingItem } from './doctorListing';
import type { PagedResponse } from '../../../types/pagination';

/** GET /appointment-inbox: the buckets plus who they're for and the doctor picker's choices. */
export interface ClinicAppointmentsInbox {
  inbox: BookingInbox;
  /** Whose appointments these are; null = the whole clinic. */
  doctorId: string | null;
  doctors: { id: string; name: string }[];
  historyDays: number;
  upcomingDays: number;
}

/** The paged tabs (medopd-docs 117). */
export type ClinicInboxTab = 'requests' | 'upcoming' | 'past' | 'cancelled' | 'notConfirmed' | 'all';

/** Filters for one page of a tab: several doctors at once; IN_PERSON / VIDEO; statuses narrow the All tab only. */
export interface ClinicInboxFilter {
  doctorIds?: string[];
  mode?: 'IN_PERSON' | 'VIDEO';
  statuses?: string[];
  from?: string;
  to?: string;
}

/** GET /appointment-inbox/summary: each tab's count, the stat cards' figures and the doctor picker. */
export interface ClinicInboxSummary {
  doctorId: string | null;
  doctors: { id: string; name: string }[];
  historyDays: number;
  upcomingDays: number;
  counts: BookingCounts;
  /** Requests to answer for the chosen doctors, whatever the in-clinic / video filter (the Requests badge). */
  pendingRequests: number;
  from?: string | null;
  to?: string | null;
}

export type RecurrenceFrequency = 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY' | 'QUARTERLY';
export type RecurrenceStatus = 'ACTIVE' | 'PAUSED' | 'CANCELLED';

export interface CreateRecurringRuleRequest {
  patientId: string;
  doctorId: string;
  frequency: RecurrenceFrequency;
  /** JSON, e.g. {"days":["monday"]} for WEEKLY/BIWEEKLY or {"dayOfMonth":15} for MONTHLY. */
  recurrencePattern: string;
  startDate: string;
  endDate?: string;
  notes?: string;
}

/** Mirrors the backend's RecurringRuleDto. */
export interface RecurringRule {
  id: string;
  doctorId: string;
  doctorName?: string | null;
  patientId: string;
  patientName?: string | null;
  frequency: RecurrenceFrequency;
  recurrencePattern: string;
  startDate: string;
  endDate?: string | null;
  status: RecurrenceStatus;
  notes?: string | null;
  createdAt?: string;
  upcoming: Array<{ id: string; date: string; status: string }>;
}

/** A pending patient-portal appointment request (Token in status REQUESTED). */
export interface AppointmentRequest {
  id: string;
  patientName?: string;
  patientMobile?: string;
  doctorId: string;
  doctorName?: string;
  visitDate: string;
  timeSlot?: string;
  visitType?: string;
  status: AppointmentStatus;
  appointmentReason?: string;
  requestedAt?: string;
  expiresAt?: string;
  /** Present only for requests made on the public booking page (no account needed). */
  publicBooking?: { reference: string; bookedFor: 'SELF' | 'CHILD' | 'PARENT' | 'SPOUSE' | 'OTHER'; needsReview: boolean } | null;
}

export const appointmentsModule = {
  create: (data: CreateAppointmentRequest) =>
    fetchApi<Appointment>('/api/appointments', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  checkForDuplicate: (patientId: string, doctorId: string, visitDate: string) =>
    fetchApi<{
      found: boolean;
      message: string;
      tokenId?: string;
      tokenNumber?: number;
      status?: string;
      createdAt?: string;
    }>(`/api/appointments/check-duplicate${buildQS({ patientId, doctorId, visitDate })}`),

  getTodayByDoctor: (doctorId: string) =>
    fetchApi<Appointment[]>(`/api/appointments/today?doctorId=${doctorId}`),

  getNextByDoctor: (doctorId: string) =>
    fetchApi<Appointment>(`/api/appointments/next?doctorId=${doctorId}`),

  updateStatus: (id: string, status: AppointmentStatus) =>
    fetchApi<Appointment>(`/api/appointments/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),

  /**
   * Marks the patient as physically present — separate from status. A token can exist before the
   * patient arrives (a follow-up booked ahead, a patient-portal self-booking); the front-desk
   * walk-in flow calls this immediately after booking since staff is looking at the patient right
   * then. Idempotent on the backend.
   */
  markArrived: (id: string) =>
    fetchApi<Appointment>(`/api/appointments/${id}/arrive`, { method: 'PATCH' }),

  /** Takes back "arrived" pressed by mistake — refused (409) once the consultation has started (medopd-docs 128). */
  undoArrived: (id: string) =>
    fetchApi<Appointment>(`/api/appointments/${id}/arrive`, { method: 'DELETE' }),

  /**
   * The patient didn't come (medopd-docs 128): a confirmed, not-started, not-arrived appointment whose time has
   * passed (today after its start, or a past day). Refusals come back as 409 with the reason.
   */
  markNoShow: (id: string) =>
    fetchApi<Appointment>(`/api/appointments/${id}/no-show`, { method: 'PATCH' }),

  getRange: (startDate: string, endDate: string, doctorId?: string) =>
    fetchApi<Appointment[]>(`/api/appointments/range${buildQS({ startDate, endDate, doctorId })}`),

  getAll: (date?: string, doctorId?: string) =>
    fetchApi<Appointment[]>(`/api/appointments${buildQS({ date, doctorId })}`),

  // ── The clinic's appointments in tabs (Requests / Upcoming / Past / Cancelled / Not confirmed) ──
  /** Doctors always get their own; staff get the whole clinic, or one doctor's with doctorId. */
  /** {@code range}: the days Past / Cancelled / Not confirmed and All cover (medopd-docs 95). */
  inbox: (doctorId?: string, range?: { from: string; to: string }) =>
    fetchApi<ClinicAppointmentsInbox>(`/api/appointment-inbox${buildQS({ doctorId, from: range?.from, to: range?.to })}`),

  /** One page of one tab (medopd-docs 117). */
  inboxPage: (tab: ClinicInboxTab, filter: ClinicInboxFilter, page: number, size: number) =>
    fetchApi<PagedResponse<BookingItem>>(`/api/appointment-inbox${buildQS({ ...filter, tab, page, size })}`),

  /** The counts and the doctor picker for the same filters (statuses aside). */
  inboxSummary: (filter: Omit<ClinicInboxFilter, 'statuses'>) =>
    fetchApi<ClinicInboxSummary>(`/api/appointment-inbox/summary${buildQS(filter)}`),

  // ── Patient-portal appointment requests (pending confirmation) ──
  listRequests: (date?: string, doctorId?: string) =>
    fetchApi<AppointmentRequest[]>(`/api/appointments/requests${buildQS({ date, doctorId })}`),

  confirmRequest: (id: string) =>
    fetchApi<AppointmentRequest>(`/api/appointments/${id}/confirm`, { method: 'POST' }),

  declineRequest: (id: string, reason?: string) =>
    fetchApi<AppointmentRequest>(`/api/appointments/${id}/decline`, {
      method: 'POST',
      body: JSON.stringify({ reason: reason ?? '' }),
    }),

  getById: (id: string) =>
    fetchApi<Appointment>(`/api/appointments/${id}`),

  delete: (id: string) =>
    fetchApi(`/api/appointments/${id}`, { method: 'DELETE' }),

  print: (id: string) =>
    fetchGetFileApi(`/api/appointments/${id}/print`),

  // Recurring appointments (RecurringAppointmentController, /api/v1/recurring-appointments).
  // The old paths here (/api/recurring-appointments, POST pause/resume/cancel) all 404'd.
  recurringAppointments: {
    create: (data: CreateRecurringRuleRequest) =>
      fetchApi<RecurringRule>('/api/v1/recurring-appointments', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    /** Rules in the active clinic for a patient and/or doctor (at least one is required). */
    list: (filter: { patientId?: string; doctorId?: string }) => {
      const params = new URLSearchParams();
      if (filter.patientId) params.set('patientId', filter.patientId);
      if (filter.doctorId) params.set('doctorId', filter.doctorId);
      return fetchApi<RecurringRule[]>(`/api/v1/recurring-appointments?${params.toString()}`);
    },
    /** One page, newest first; excludeCancelled leaves out cancelled plans (medopd-docs 117). */
    listPage: (filter: { patientId?: string; doctorId?: string; excludeCancelled?: boolean }, page: number, size: number) =>
      fetchApi<PagedResponse<RecurringRule>>(`/api/v1/recurring-appointments${buildQS({ ...filter, page, size })}`),
    pauseRule: (ruleId: string) =>
      fetchApi<RecurringRule>(`/api/v1/recurring-appointments/${encodeURIComponent(ruleId)}/pause`, { method: 'PATCH' }),
    resumeRule: (ruleId: string) =>
      fetchApi<RecurringRule>(`/api/v1/recurring-appointments/${encodeURIComponent(ruleId)}/resume`, { method: 'PATCH' }),
    cancelRule: (ruleId: string) =>
      fetchApi<RecurringRule>(`/api/v1/recurring-appointments/${encodeURIComponent(ruleId)}`, { method: 'DELETE' }),
  },
};
